import i18n from "../i18n";
import React, { useState } from "react";
import { Group, FileButton, Button, Text, Anchor, Tooltip } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconDownload, IconExclamationMark } from "@tabler/icons";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../hooks/userServerApi";

const GISettlementStatementImporter = ({
  form,
  disabled,
  onImportSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [api] = useServerApi();
  const { t } = useTranslation();
  const settlementId = form?.values?._id;
  const fileName = form?.values?.insurerStatement?.fileName;

  const handleUploadSuccess = (file, data) => {
    showNotification({
      title: `${file.name} imported successfully`,
      message: "Insurer statement imported",
      color: "green",
    });
    form.setFieldValue("insurerStatement", data.insurerStatement);
    form.setFieldValue("insurerCrossCheckSummary", data.insurerCrossCheckSummary);
    onImportSuccess?.(data);
  };

  const handleUploadFail = (file, error) => {
    showNotification({
      title: `${file?.name || "Import"} failed`,
      color: "red",
      icon: <IconExclamationMark size={18} />,
      message: error?.msg || error?.message || String(error),
    });
  };

  const handleFileUpload = async (file) => {
    if (!file || !settlementId) return;
    setLoading(true);
    const result = await api.GISettlement.importStatement(settlementId, file);
    setLoading(false);
    if (result?.success) {
      handleUploadSuccess(file, result.data);
    } else {
      handleUploadFail(file, result?.error);
    }
  };

  const handleDownloadStatement = async () => {
    if (!settlementId || !fileName) return;
    setDownloading(true);
    const result = await api.GISettlement.downloadStatement(
      settlementId,
      fileName
    );
    setDownloading(false);
    if (!result?.success) {
      showNotification({
        title: "Download failed",
        color: "red",
        icon: <IconExclamationMark size={18} />,
        message: result?.error || "Unable to download statement file",
      });
    }
  };

  return (
    <Group spacing="md">
      <FileButton
        onChange={handleFileUpload}
        accept="application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        disabled={disabled || !settlementId}
      >
        {(props) => (
          <Tooltip
            label={t(
              "giSettlement.crossCheck.importStatementHint",
              "Import the insurer statement"
            )}
            withArrow
          >
            <Button {...props} size="xs" loading={loading}>
              {t("giSettlement.crossCheck.importStatement", "Import Statement")}
            </Button>
          </Tooltip>
        )}
      </FileButton>

      <Anchor
        href={api.GISettlement.downloadStatementTemplateUrl}
        target="_blank"
        size="xs"
      >
        <Group spacing={4}>
          <IconDownload size="1rem" />
          <Text size="xs">{i18n.t("Download Template")}</Text>
        </Group>
      </Anchor>

      {fileName && (
        <Text
          size="xs"
          c="dimmed"
          sx={{ cursor: downloading ? "wait" : "pointer" }}
          onClick={handleDownloadStatement}
        >
          {fileName}
        </Text>
      )}
    </Group>
  );
};

export default GISettlementStatementImporter;
